import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Button, Snackbar, Alert, useTheme, useMediaQuery, TextField, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ModalPopup from "../../components/ui/ModalPopup";
import OtpInputForm from "../common-features/OtpInputForm";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import applicantDetailsServices from "../../services/api/service/applicantDetailsService";
import applicationDetailsServices from "../../services/api/service/applicationDetails";
import losServices from "../../services/api/service/losService";
import { encryptData } from "../../utils/crypto";
import { getSanitizedPublicKeyFromResponse } from "../../utils/piiKey";
import { callCreateApi, callSaveCoApplicantApi, maskValue } from "../../components/ui/StepperDyanmicForm/helper";
import greenCheckVerified from "../../assets/svgIcons/green-check-verified.svg";
import arrowRightVerify from "../../assets/svgIcons/arrow-right-verify.svg";
import type { RootState } from "../../services/redux/store";
import { persistor } from "../../services/redux/store";
import {
  setAdditionalSummaryConsentComplete,
  setAdditionalSummaryActiveTab,
  setSelectedIndex,
} from "../../services/redux/slices/tabsSlice";
import {
  setAadhaarApplicationNo,
  setAadhaarApplicationNoCoApplicant,
  updateFormData,
  setAadhaarVerifiedDetails,
  setAadhaarVerifiedDetailsCoApplicant,
  setIsDocumentVerified,
  setIsDocumentVerifiedCoApplicant,
  setDocumentApplicantType,
  setCoApplicantVerification,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { useTriggerData } from "../../hooks/useTriggerData";
import { isDeviceTypeMobile } from "../../utils/helpers";
import { trackCustomerJourneyEvent } from "../../utils/ga";
import { getGAExtendedJourneyPrefix } from "../../components/ui/StepperDyanmicForm/helper";
interface ConsentDetailsProps {
  formData: any;
  mainApplicantDetails?: any;
  coApplicantDetails?: any;
  trackingId?: string;
  handleFetchApplicationDetail?: () => void | Promise<void>;
  onContinue?: () => void;
  partnerStartedJourney?: boolean;
  pageTypeSummary?: string;
}

type ApplicantConsentRow = {
  key: string;
  applicantType: string;
  fullName: string;
  email: string;
  mobile: string;
  emailVerified: boolean;
  mobileVerified: boolean;
  panVerified: boolean;
  aadhaarVerified: boolean;
  panValue: string;
  aadhaarValue: string;
  coApplicantId?: number | string;
};

const parseBool = (v: any) => v === true || v === "true";

const ConsentDetails: React.FC<ConsentDetailsProps> = ({
  formData,
  mainApplicantDetails,
  coApplicantDetails,
  trackingId,
  handleFetchApplicationDetail,
  onContinue,
  partnerStartedJourney,
  pageTypeSummary 
}) => {
  const dispatch = useDispatch();
  const customerConsentTime = useSelector(
    (state: RootState) => state.stepper.customerConsentTime,
  );
  const mainApplicantId = useSelector(
    (state: RootState) =>
      (state.stepper as any)?.fetchUserApplicationData?.applicantId ||
      (state.stepper as any)?.applicantId ||
      0,
  );
  const applicationId = useSelector(
    (state: RootState) =>
      (state.stepper as any)?.fetchUserApplicationData?.applicationId ||
      (state.stepper as any)?.applicationId ||
      0,
  );
  const horizontalSteps = useSelector((state: RootState) => (state.stepper as any)?.horizontalSteps || []);
  const fetchedApplicationData = useSelector((state: RootState) => (state.stepper as any)?.fetchUserApplicationData || {});
  const fetchedApplicationSteps = fetchedApplicationData?.horizontalSteps || [];
  const { templateId } = useSelector((state: RootState) => (state.stepper as any) || "");
  const resolvedTemplateId = templateId || fetchedApplicationData?.templateId || "";
  const selectedProduct = useSelector((state: RootState) => (state.stepper as any)?.selectedProduct || "SLI");
  const authProfileId = useSelector((state: RootState) => (state.auth as any)?.userProfile?.id || "");
  const documentApplicantType = useSelector((state: RootState) => (state.stepper as any)?.documentApplicantType || "");
  const applicationStatus = useSelector((state: RootState) => (state.tabs as any)?.applicationStatus || "");
  const isSubmittedApplication = [
    "application submitted",
    "submitted",
  ].includes(String(fetchedApplicationData?.status || applicationStatus || "").toLowerCase());
  const { isTriggerDataEmpty, triggerData }: any = useTriggerData();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));

  const rows = useMemo<ApplicantConsentRow[]>(() => {
      const list: ApplicantConsentRow[] = [];

      if (partnerStartedJourney) {
        // ----------------------------------------------------
        // PARTNER STARTED JOURNEY: TRUE
        // ----------------------------------------------------

        // Helper to get value from applicantDetailsData (array of field objects)
        const getApplicantVal = (name: string) =>
          mainApplicantDetails?.find((item: any) => item.name === name)?.value;

        const firstName = getApplicantVal("firstName") || "";
        const lastName = getApplicantVal("lastName") || "";
        const fullName = `${firstName} ${lastName}`.trim() || "Applicant";

        // Primary Applicant
        list.push({
          key: "applicant",
          applicantType: "Applicant",
          fullName: fullName,
          email: String(getApplicantVal("email") || ""),
          mobile: String(getApplicantVal("phone_number") || ""),
          emailVerified: parseBool(getApplicantVal("isEmailVerified")),
          mobileVerified: parseBool(getApplicantVal("isMobileVerified")),
          panVerified: parseBool(getApplicantVal("isPanVerified") ?? getApplicantVal("panVerified")),
          aadhaarVerified: parseBool(getApplicantVal("isAadhaarVerified") ?? getApplicantVal("aadhaarVerified")),
          panValue: String(getApplicantVal("pan") ?? getApplicantVal("panNumber") ?? ""),
          aadhaarValue: String(getApplicantVal("aadhaar") ?? getApplicantVal("aadhaarNumber") ?? ""),
        });
  
        // Co-Applicants from coApplicantDetails object
        if (coApplicantDetails && typeof coApplicantDetails === "object") {
          Object.keys(coApplicantDetails).forEach((key) => {
            const coObj = coApplicantDetails[key];
            const match = key.match(/coapplicant(\d+)/i);
            const index = match ? match[1] : "";
            let type=coObj?.coApplicantType.split("_");
            if (coObj) {
              list.push({
                key: `coapplicant${type[2]}`,
                applicantType: `Co-Applicant ${type[2]}`,
                fullName: String(coObj.coApplicantName || `Co-Applicant ${index}`).trim(),
                email: String(coObj.coApplicantEmail || ""),
                mobile: String(coObj.coApplicantPhone || ""),
                emailVerified: coObj.isEmailVerified ===undefined ? false : parseBool(coObj.isEmailVerified ?? coObj.IsEmailVerified),
                mobileVerified: coObj.isMobileVerified ===undefined ? false : parseBool(coObj.isMobileVerified ?? coObj.IsMobileVerified),
                panVerified: parseBool(coObj.isPanVerified ?? coObj.IsPanVerified ?? coObj.panVerified),
                aadhaarVerified: parseBool(coObj.isAadhaarVerified ?? coObj.IsAadhaarVerified ?? coObj.aadhaarVerified),
                panValue: String(coObj.pan ?? coObj.panNumber ?? ""),
                aadhaarValue: String(coObj.aadhaar ?? coObj.aadhaarNumber ?? coObj.AadhaarNumber ?? ""),
                coApplicantId: coObj.coApplicantId,
              });
            }
          });
        }
      } else {
        // ----------------------------------------------------
        // PARTNER STARTED JOURNEY: FALSE (ORIGINAL LOGIC)
        // ----------------------------------------------------
        const appFirstName =
          mainApplicantDetails?.firstName ||
          mainApplicantDetails?.studentDetailsFirstName ||
          formData?.firstName ||
          formData?.studentDetailsFirstName ||
          "Applicant";
        const appLastName =
          mainApplicantDetails?.lastName ||
          mainApplicantDetails?.studentDetailsLastName ||
          formData?.lastName ||
          formData?.studentDetailsLastName ||
          "";

        list.push({
          key: "applicant",
          applicantType: "Applicant",
          fullName: `${appFirstName} ${appLastName}`.trim(),
          email: String(mainApplicantDetails?.email || formData?.email || ""),
          mobile: String(
            mainApplicantDetails?.phone_number ||
              formData?.phone_number ||
              formData?.studentDetailsPhone_number ||
              ""
          ),
          emailVerified: parseBool(formData?.isEmailVerified),
          mobileVerified: parseBool(formData?.isMobileVerified),
          panVerified: parseBool(formData?.isPanVerified ?? formData?.panVerified),
          aadhaarVerified: parseBool(formData?.isAadhaarVerified ?? formData?.aadhaarVerified),
          panValue: String(formData?.pan ?? formData?.panNumber ?? ""),
          aadhaarValue: String(formData?.aadhaar ?? formData?.aadhaarNumber ?? ""),
        });

        const coKeys = Object.keys(formData || {}).filter((k) =>
          /^basicCoApplicant\d+Email$/.test(k)
        );

        const coKeysFromDetails = Object.keys(coApplicantDetails || {})
          .filter((k) => /^coapplicant\d+$/i.test(k))
          .map((k) => {
            const match = k.match(/coapplicant(\d+)/i);
            return match ? Number(match[1]) : NaN;
          })
          .filter((n) => !Number.isNaN(n));

        const coNumbers = coKeys
          .map((k) => Number((k.match(/^basicCoApplicant(\d+)Email$/) || [])[1]))
          .concat(coKeysFromDetails)
          .filter((n, idx, arr) => arr.indexOf(n) === idx)
          .filter((n) => !Number.isNaN(n))
          .sort((a, b) => a - b);

        coNumbers.forEach((n) => {
          const coObj = coApplicantDetails?.[`coapplicant${n}`] || {};
          const firstName =
            coObj?.FirstName ||
            formData?.[`basicCoApplicant${n}FirstName`] ||
            `Co-Applicant ${n}`;
          const lastName =
            coObj?.LastName || formData?.[`basicCoApplicant${n}LastName`] || "";

          list.push({
            key: `coapplicant${n}`,
            applicantType: `Co-Applicant ${n}`,
            fullName: `${firstName} ${lastName}`.trim(),
            email: String(
              coObj?.Email || formData?.[`basicCoApplicant${n}Email`] || ""
            ),
            mobile: String(
              coObj?.PhoneNumber ||
                formData?.[`basicCoApplicant${n}PhoneNumber`] ||
                ""
            ),
            emailVerified: parseBool(
              formData?.[`basicCoApplicant${n}IsEmailVerified`] ??
              formData?.[`coApplicant${n}IsEmailVerified`] ?? 
                coObj?.IsEmailVerified ??
                coObj?.isEmailVerified
            ),
            mobileVerified: parseBool(
              formData?.[`basicCoApplicant${n}IsMobileVerified`] ??
              formData?.[`coApplicant${n}IsMobileVerified`] ??
                coObj?.IsMobileVerified ??
                coObj?.isMobileVerified
            ),
            panVerified: parseBool(
              formData?.[`basicCoApplicant${n}IsPanVerified`] ??
              formData?.[`basicCoApplicant${n}PanVerified`] ??
              coObj?.IsPanVerified ??
              coObj?.isPanVerified
            ),
            aadhaarVerified: parseBool(
              formData?.[`basicCoApplicant${n}IsAadhaarVerified`] ??
              formData?.[`basicCoApplicant${n}AadhaarVerified`] ??
              coObj?.IsAadhaarVerified ??
              coObj?.isAadhaarVerified
            ),
            panValue: String(
              formData?.[`basicCoApplicant${n}Pan`] ??
              formData?.[`basicCoApplicant${n}PanNumber`] ??
              coObj?.pan ??
              coObj?.panNumber ??
              ""
            ),
            aadhaarValue: String(
              formData?.[`basicCoApplicant${n}AadhaarNumber`] ??
              formData?.[`basicCoApplicant${n}Aadhaar`] ??
              coObj?.aadhaar ??
              coObj?.aadhaarNumber ??
              coObj?.AadhaarNumber ??
              ""
            ),
          });
        });
      }

      return list;
    }, [
      partnerStartedJourney,
      formData,
      mainApplicantDetails,
      coApplicantDetails,
    ]);

  const [emailVerifiedMap, setEmailVerifiedMap] = useState<Record<string, boolean>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.emailVerified])),
  );
  const [mobileVerifiedMap, setMobileVerifiedMap] = useState<Record<string, boolean>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.mobileVerified])),
  );
  const [panVerifiedMap, setPanVerifiedMap] = useState<Record<string, boolean>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.panVerified])),
  );
  const [aadhaarVerifiedMap, setAadhaarVerifiedMap] = useState<Record<string, boolean>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.aadhaarVerified])),
  );
  const [panValueMap, setPanValueMap] = useState<Record<string, string>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.panValue])),
  );
  const [adhaarPanValueMap, setAdhaarPanValueMap] = useState<Record<string, string>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.aadhaarValue])),
  );
  const [identityLoading, setIdentityLoading] = useState<string>("");
  const aadhaarCallbackProcessingRef = useRef<Set<string>>(new Set());
  const normalizeField = (field: any) => ({
    ...field,
    id: field?.id ?? field?.formFieldId,
    name: field?.name || field?.fieldKey || Object.keys(field || {})[0],
  });
  const identityFields = useMemo(() => (
    [...horizontalSteps, ...fetchedApplicationSteps]
      .flatMap((step: any) => step?.verticalSteps || [])
      .flatMap((step: any) => step?.fields || [])
      .map(normalizeField)
      .filter((field: any) => field?.id && field?.name)
      .filter((field: any, index: number, fields: any[]) =>
        fields.findIndex((item: any) => item?.id === field?.id || item?.name === field?.name) === index
      )
  ), [horizontalSteps, fetchedApplicationSteps]);

  const shouldShowIdentity = (row: ApplicantConsentRow) => {
    if (row.key === "applicant") {
      const studentFields = identityFields.filter((field: any) =>
        /^(studentDetails|isPanVerified|isAadhaarVerified|pan|aadhaar|dob)/i.test(String(field?.name || ""))
      );
      const populatedFields = studentFields.filter((field: any) => {
        const value = formData?.[field.name];
        return value !== undefined && value !== null && String(value).trim() !== "";
      });
      return populatedFields.length > 4 || row.panVerified || row.aadhaarVerified || Boolean(row.panValue);
    }

    const coNumber = row.key.match(/coapplicant(\d+)/i)?.[1];
    const prefix = `basicCoApplicant${coNumber}`;
    const populatedFields = Object.keys(formData || {}).filter((key) =>
      key.startsWith(prefix) && formData[key] !== undefined && formData[key] !== null && String(formData[key]).trim() !== ""
    );
    return populatedFields.length > 4 || row.panVerified || row.aadhaarVerified || Boolean(row.panValue);
  };
  const visibleRows = useMemo<ApplicantConsentRow[]>(() => {
    let filtered = rows;
    if (!isTriggerDataEmpty) {
      if (triggerData?.toUser) {
        filtered = rows.filter((row) => row.key === "applicant");
      } else {
        const coApplicantNumber = String(triggerData?.coApplicantType || "").match(/\d+/)?.[0];
        filtered = coApplicantNumber
          ? rows.filter((row) => row.key === `coapplicant${coApplicantNumber}`)
          : rows;
      }
    }

    // Partner-started journey: overlay the authoritative verification status
    // fetched from getCoApplicantBasicDetails once it resolves, instead of
    // relying on the (sometimes unreliable) flags baked into `rows`. Other
    // journeys keep using the row's own flags as before.
    if (!partnerStartedJourney) return filtered;
    return filtered.map((row) => ({
      ...row,
      emailVerified: emailVerifiedMap[row.key] ?? row.emailVerified,
      mobileVerified: mobileVerifiedMap[row.key] ?? row.mobileVerified,
    }));
  }, [
    rows,
    isTriggerDataEmpty,
    triggerData,
    partnerStartedJourney,
    emailVerifiedMap,
    mobileVerifiedMap,
  ]);

  const [currentAppType, setCurrentAppType] = useState<string>("");
  const applicationSource = useSelector((state: RootState) => state.stepper?.applicationSource);

  const extendedJourneyPrefix = useMemo(
      () => getGAExtendedJourneyPrefix(applicationSource),
      [applicationSource]
    );

  useEffect(() => {
    setEmailVerifiedMap((prev) => {
      const next = Object.fromEntries(rows.map((r) => [r.key, r.emailVerified]));
      Object.keys(prev).forEach((key) => {
        if (prev[key]) next[key] = true;
      });
      return next;
    });

    setMobileVerifiedMap((prev) => {
      const next = Object.fromEntries(rows.map((r) => [r.key, r.mobileVerified]));
      Object.keys(prev).forEach((key) => {
        if (prev[key]) next[key] = true;
      });
      return next;
    });
    setPanVerifiedMap((prev) => {
      const next = Object.fromEntries(rows.map((r) => [r.key, r.panVerified]));
      Object.keys(prev).forEach((key) => {
        if (prev[key]) next[key] = true;
      });
      return next;
    });
    setAadhaarVerifiedMap((prev) => {
      const next = Object.fromEntries(rows.map((r) => [r.key, r.aadhaarVerified]));
      Object.keys(prev).forEach((key) => {
        if (prev[key]) next[key] = true;
      });
      return next;
    });
    setPanValueMap((prev) => ({
      ...Object.fromEntries(rows.map((r) => [r.key, r.panValue])),
      ...prev,
    }));
    setAdhaarPanValueMap((prev) => ({
      ...Object.fromEntries(rows.map((r) => [r.key, r.aadhaarValue])),
      ...prev,
    }));
  }, [rows]);

  // For partner-started journeys, coApplicantDetails' email/mobile-verified
  // flags aren't always reliable, so re-confirm each co-applicant's status
  // straight from getCoApplicantBasicDetails (same source ApplicantTabs/
  // CoApplicantTabs use) and layer any "true" results on top of the maps.
  useEffect(() => {
    if (!partnerStartedJourney) return;

    const coApplicantRows = rows.filter(
      (row) => row.key !== "applicant" && row.coApplicantId,
    );
    if (coApplicantRows.length === 0) return;

    let isMounted = true;

    (async () => {
      await Promise.all(
        coApplicantRows.map(async (row) => {
          try {
            const res: any = await applicationDetailsServices.getCoApplicantBasicDetails(
              String(row.coApplicantId),
              trackingId || "",
            );
            const basicArr = Array.isArray(res?.data?.data) ? res.data.data : [];

            if (!isMounted) return;

            // Field names carry their own co-applicant number (e.g.
            // "coApplicant2IsEmailVerified") — map each field to the row it
            // actually belongs to instead of assuming it matches whichever
            // row triggered this call.
            basicArr.forEach((f: any) => {
              const fieldName = String(f?.name || f?.key || "");
              const coAppMatch = fieldName.match(
                /coApplicant(\d+)Is(Email|Mobile)Verified$/i,
              );
              const targetKey = coAppMatch
                ? `coapplicant${coAppMatch[1]}`
                : "";
              if (!parseBool(f?.value)) return;

              if (/IsEmailVerified$/i.test(fieldName) && targetKey) {
                setEmailVerifiedMap((prev) => ({ ...prev, [targetKey]: true }));
              } else if (/IsMobileVerified$/i.test(fieldName) && targetKey) {
                setMobileVerifiedMap((prev) => ({ ...prev, [targetKey]: true }));
              }
            });
          } catch (err) {
            console.error(
              `Failed to fetch co-applicant basic details for ${row.key}:`,
              err,
            );
          }
        }),
      );
    })();

    return () => {
      isMounted = false;
    };
  }, [rows, partnerStartedJourney, trackingId]);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [otpModal, setOtpModal] = useState<{
    open: boolean;
    type: "email" | "mobile";
    rowKey: string;
    value: string;
    key: string;
    otpTime: string;
  }>({
    open: false,
    type: "email",
    rowKey: "",
    value: "",
    key: "",
    otpTime: "",
  });

  const hasConsentForRow = (rowKey: string) => {
    if (rowKey === "applicant") {
      return !!customerConsentTime;
    }

    const n = Number((rowKey.match(/coapplicant(\d+)/i) || [])[1]);
    if (!n) return false;

    const coObj = coApplicantDetails?.[`coapplicant${n}`] || {};
    return Boolean(
      coObj?.coApplicantConsentTime ||
        coObj?.consentTime ||
        coObj?.customerConsentTime ||
        formData?.[`basicCoApplicant${n}ConsentTime`] ||
        formData?.[`basicCoApplicant${n}CustomerConsentTime`],
    );
  };

  const handleRequestOtp = async (
    row: ApplicantConsentRow,
    type: "email" | "mobile",
  ) => {
    try {
      const payload: any = {
        name: row.fullName || row.applicantType,
      };
      setCurrentAppType(row?.applicantType);
      if (type === "email") payload.email = row.email?.toLowerCase();
      else payload.mobileNumber = row.mobile;

      const res: any = await eligibilityCheckServices.sendOtp(payload);
      if (res?.data?.status === "fail") {
        setSnackbar({ open: true, severity: "error", message: res?.data?.message || "Failed to send OTP" });
        return;
      }

      setOtpModal({
        open: true,
        type,
        rowKey: row.key,
        value: type === "email" ? row.email : row.mobile,
        key: res?.data?.data?.value || "",
        otpTime: res?.data?.data?.otpTime || "",
      });
    } catch {
      setSnackbar({ open: true, severity: "error", message: "Unable to send OTP" });
    }
  };

  // Resolve the applicantId/event-naming context for a given row so the LOS
  // verification call carries the right ids for the applicant vs a co-applicant.
  const resolveVerificationContext = async (row: ApplicantConsentRow) => {
    if (row.key === "applicant") {
      return {
        applicantId: Number(mainApplicantId) || 0,
        eventPrefix: "APPLICANT",
        userApplicantType: "Applicant",
      };
    }

    const coMatch = row.key.match(/coapplicant(\d+)/i);
    const n = coMatch ? coMatch[1] : "";
    // Prefer the co-applicant's own applicantId already carried on the row
    // (populated when rows are built) over a fresh get-coapplicant-details
    // lookup — that extra round-trip could resolve to the wrong applicant,
    // which was causing the co-applicant's verified email to get written
    // onto the main applicant's record instead of the co-applicant's.
    let coApplicantId = Number(row.coApplicantId) || 0;
    if (!coApplicantId) {
      try {
        const resp: any = await losServices.getCoApplicantDetails({
          trackingId,
          coApplicantType: `Co-Applicant ${n}`,
        });
        coApplicantId = Number(resp?.data?.data?.applicantId) || 0;
      } catch (err) {
        console.error("Failed to resolve co-applicant applicantId:", err);
      }
    }

    // A co-applicant can never legitimately share the main applicant's id.
    // If resolution above lands on it anyway (e.g. the backend couldn't find
    // a co-applicant record for this trackingId and defaulted to the primary
    // applicant), treat it as unresolved so we don't sync verification onto
    // the wrong record.
    if (coApplicantId && coApplicantId === (Number(mainApplicantId) || 0)) {
     
      coApplicantId = 0;
    }

    return {
      applicantId: coApplicantId,
      eventPrefix: `COAPPLICANT${n}`,
      userApplicantType: `Co-Applicant ${n}`,
    };
  };

  const handleVerifyOtp = async (otp: string) => {
    try {
      const row = visibleRows.find((r) => r.key === otpModal.rowKey);
      if (!row) return;

      const userType = row.applicantType;
      const payload: any = {
        type: otpModal.type,
        key: otpModal.key,
        userType,
      };
      if (trackingId) payload.trackingId = trackingId;

      let page = "";
      let event = "";

      if (otpModal.type === "email") {
        payload.email = otpModal.value;
        payload.emailOtp = otp;
        payload.emailOtpCrtdAt = otpModal.otpTime;
        page = currentAppType?.includes("Co-Applicant") ? "Extended Journey_coapplicant email Verification" : "Extended Journey_applicant email Verification"
        event = currentAppType?.includes("Co-Applicant") ? `${extendedJourneyPrefix}_COAPPEMAIL VERIFICATION_PAGE17` : `${extendedJourneyPrefix}_APPLICANTEMAIL VERIFICATION_PAGE17`
      } else {
        payload.mobileNumber = otpModal.value;
        payload.mobileOtp = otp;
        payload.mobileOtpCrtdAt = otpModal.otpTime;
        page = currentAppType?.includes("Co-Applicant") ? "Extended Journey_coapplicant mobile Verification" : "Extended Journey_applicant mobile Verification";
        event = currentAppType?.includes("Co-Applicant") ? `${extendedJourneyPrefix}_COAPPMOBILE VERIFICATION_PAGE17` : `${extendedJourneyPrefix}_APPLICANTMOBILE VERIFICATION_PAGE17`
      }
      
      const res: any = await eligibilityCheckServices.verifyOtp(payload);
      if (res?.data?.status !== "success") {
        setSnackbar({ open: true, severity: "error", message: res?.data?.message || "OTP verification failed" });
        return;
      }

      const { applicantId, eventPrefix, userApplicantType } =
        await resolveVerificationContext(row);

      const verificationPayload = {
        verificationType: otpModal.type === "email" ? "Email" : "Mobile",
        applicantId: Number(applicantId) || 0,
        applicationId: Number(applicationId) || 0,
        trackingId: trackingId,
        eventName: `${eventPrefix}_${
          otpModal.type === "email" ? "EMAIL" : "MOBILE"
        }_VERIFICATION`,
        userApplicantType,
      };

      // Also require a resolved applicantId — sending 0/unresolved lets the
      // backend fall back to the primary applicant's record, which is what
      // was causing a co-applicant's OTP verification to mark the main
      // applicant as email/mobile verified too.
      if (verificationPayload.applicationId > 0 && verificationPayload.applicantId > 0) {
        try {
          await losServices.emailAndMobileVerification(verificationPayload);
          trackCustomerJourneyEvent(
            event,
            "",
            trackingId || "",
            {
              label: event,
              category: event,
              action: event,
              page: page,
            }
          );
        } catch (err) {
          console.error("Failed to sync verification with LOS:", err);
        }
      } else {
        console.error(
          "Skipped LOS verification sync: applicantId could not be resolved for",
          otpModal.rowKey,
        );
      }

      if (otpModal.type === "email") {
        setEmailVerifiedMap((prev) => ({ ...prev, [otpModal.rowKey]: true }));
      } else {
        setMobileVerifiedMap((prev) => ({ ...prev, [otpModal.rowKey]: true }));
      }
      const coMatch = otpModal.rowKey.match(/coapplicant(\d+)/i);
      if (coMatch) {
        const n = coMatch[1];
        // Redux's coApplicantVerification map is keyed by 0-based string index.
        const reduxIdx = String(Number(n) - 1);
        if (otpModal.type === "email") {
          dispatch(
            setCoApplicantVerification({
              index: reduxIdx,
              data: {
                emailVerified: true,
                lastVerifiedEmail: otpModal.value,
              },
            }),
          );
        } else {
          dispatch(
            setCoApplicantVerification({
              index: reduxIdx,
              data: {
                mobileVerified: true,
                lastVerifiedMobileNumber: otpModal.value,
              },
            }),
          );
        }
      } 
      if (coMatch) {
        const n = coMatch[1];
        const fieldKey =
          otpModal.type === "email"
            ? `basicCoApplicant${n}IsEmailVerified`
            : `basicCoApplicant${n}IsMobileVerified`;
        try {
          await applicantDetailsServices.updateCoApplicantFormData({
            trackingId,
            coApplicantType: `Co-Applicant ${n}`,
            key: fieldKey,
            value: true,
          });
        } catch (err) {
          console.error("Failed to update co-applicant form data:", err);
        }
      }

      setOtpModal((prev) => ({ ...prev, open: false }));
      setSnackbar({ open: true, severity: "success", message: "Verified successfully" });
    } catch {
      setSnackbar({ open: true, severity: "error", message: "OTP verification failed" });
    }
  };

  const getRowDob = (row: ApplicantConsentRow) => {
    const coNumber = row.key.match(/coapplicant(\d+)/i)?.[1];
    return String(
      row.key === "applicant"
        ? formData?.dob || formData?.studentDetailsDob ||
          (Array.isArray(mainApplicantDetails)
            ? mainApplicantDetails.find((field: any) => /dob/i.test(field?.name))?.value
            : "") || ""
        : formData?.[`basicCoApplicant${coNumber}dob`] || formData?.[`basicCoApplicant${coNumber}Dob`] || formData?.[`coApplicant${coNumber}Dob`] || ""
    );
  };

  const handleVerifyPan = async (row: ApplicantConsentRow) => {
    const panNumber = String(panValueMap[row.key] || "").trim().toUpperCase();
    const dob = getRowDob(row);
    if (!panNumber || !dob) {
      setSnackbar({ open: true, severity: "error", message: "Please provide PAN and Date of Birth before verification." });
      return;
    }

    setIdentityLoading(`pan:${row.key}`);
    try {
      const piiKeyResponse: any = await losServices.getPiiKey();
      const publicKey = getSanitizedPublicKeyFromResponse(piiKeyResponse?.data?.p_k);
      if (!publicKey) throw new Error("Unable to load PAN verification key.");
      const formattedDob = /^\d{2}\/\d{2}\/\d{4}$/.test(dob)
        ? dob
        : new Date(dob).toLocaleDateString("en-GB");
      const { applicantId, userApplicantType } = await resolveVerificationContext(row);
      const payload = await encryptData(
        {
          panNumber,
          fullName: row.fullName,
          dob: formattedDob,
          trackingId,
          applicantType: userApplicantType,
          applicantId,
        },
        ["panNumber"],
        publicKey
      );
      const response: any = await applicantDetailsServices.verifyPan(payload);
      const result = response?.data?.data || {};
      if (String(result?.dateOfBirth || "").toLowerCase() === "invalid") {
        throw new Error("DOB mismatch. Please ensure the Date of Birth matches the one linked to this PAN.");
      }
      if (String(result?.panStatus || "").toLowerCase() === "invalid") {
        throw new Error("PAN verification failed. Please check the PAN details.");
      }
      await persistIdentityVerification(row, "PAN", { panNumber });
      setPanValueMap((current) => ({ ...current, [row.key]: panNumber }));
      setPanVerifiedMap((current) => ({ ...current, [row.key]: true }));
      setSnackbar({ open: true, severity: "success", message: "PAN verified successfully." });
    } catch (error: any) {
      setSnackbar({ open: true, severity: "error", message: error?.response?.data?.message || error?.message || "PAN verification failed." });
    } finally {
      setIdentityLoading("");
    }
  };

  const persistIdentityVerification = async (
    row: ApplicantConsentRow,
    type: "PAN" | "ADHAAR",
    details: any = {},
  ) => {
    const formDataForSave = {
      ...(formData || {}),
      ...(row.key === "applicant"
        ? type === "PAN"
          ? { pan: details.panNumber, isPanVerified: true }
          : { isAadhaarVerified: true, aadhaar: details.documentId || formData?.aadhaar }
        : type === "PAN"
          ? {
              [`basicCoApplicant${row.key.replace("coapplicant", "")}Pan`]: details.panNumber,
              [`basicCoApplicant${row.key.replace("coapplicant", "")}IsPanVerified`]: true,
            }
          : {
              [`basicCoApplicant${row.key.replace("coapplicant", "")}IsAadhaarVerified`]: true,
              [`basicCoApplicant${row.key.replace("coapplicant", "")}AadhaarVerified`]: true,
            }),
    };
    if (type === "ADHAAR" && !details.isAadhaarConsent) {
      return;
    }

    const coNumber = row.key.replace("coapplicant", "");
    const getSectionFields = (steps: any[]) => steps
      .flatMap((step: any) => step?.verticalSteps || [])
      .filter((step: any) => {
        const label = String(step?.stepLabel || step?.verticalStepName || "").toLowerCase();
        const id = String(step?.id ?? step?.verticalStepId ?? "");
        if (row.key === "applicant") {
          return id === "4" && label === "student details";
        }
        return (id === "9" && label === "co-applicant details") ||
          (id === "11" && label.includes("co-applicant aditional details"));
      })
      .flatMap((step: any) => step?.fields || [])
      .map(normalizeField)
      .filter((field: any) => field?.id && field?.name)
      .filter((field: any) => row.key === "applicant" ||
        String(field.name).includes(`CoApplicant${coNumber}`));

    let fieldsForSave = getSectionFields([...horizontalSteps, ...fetchedApplicationSteps]);
    if (!fieldsForSave.length) {
      fieldsForSave = identityFields.filter((field: any) => row.key === "applicant"
        ? /^(studentDetails|studentDetail|pan|aadhaar|gender|father|mother|isPanVerified|isAadhaarVerified|dob)/i.test(String(field.name))
        : String(field.name).includes(`CoApplicant${coNumber}`));
    }
    let templateForSave = resolvedTemplateId;
    const hasMatchingFields = Object.keys(formDataForSave).some((key) =>
      fieldsForSave.some((field: any) => field?.name === key && field?.id)
    );

    if (!hasMatchingFields || !templateForSave) {
      try {
        const formResponse: any = await eligibilityCheckServices.getForm({
          productCode: selectedProduct,
          id: authProfileId,
          trackingId,
        });
        const formConfig = formResponse?.data?.data || {};
        const fetchedFields = getSectionFields(formConfig?.horizontalSteps || []);
        fieldsForSave = [...fieldsForSave, ...fetchedFields].filter(
          (field: any, index: number, fields: any[]) =>
            fields.findIndex((item: any) => item?.id === field?.id || item?.name === field?.name) === index
        );
        templateForSave = templateForSave || formConfig?.templateId || "";
      } catch (error) {
        console.error("Unable to load form definitions for identity save:", error);
      }
    }

    fieldsForSave = fieldsForSave.filter((field: any) => {
      const value = formDataForSave[field.name];
      return value !== undefined && value !== null && String(value).trim() !== "";
    });
    if (row.key === "applicant") {
      await callCreateApi(
        formDataForSave,
        fieldsForSave,
        trackingId || "",
        templateForSave,
        Boolean(details.isAadhaarConsent || details.isAadhaarSeeded),
       {
        ...(details.applicantInfo || {}),
        gender: formDataForSave?.gender
      },
        type,
      );
      return;
    }

    const fieldData = fieldsForSave.filter((field: any) =>
      String(field?.name || "").includes(`CoApplicant${coNumber}`)
    );
    await callSaveCoApplicantApi(
      formDataForSave,
      fieldData,
      `CoApplicant${coNumber}`,
      trackingId || "",
      Boolean(details.isAadhaarConsent || details.isAadhaarSeeded),
      type,
      details.proofOfAddress || {},
    );
  };

  const handleVerifyAadhaar = async (row: ApplicantConsentRow) => {
    if (!trackingId) return;
    dispatch(setSelectedIndex(2));
    dispatch(setAdditionalSummaryActiveTab(1));
    const typeOfApplicant = row.applicantType || "Applicant";
    dispatch(setDocumentApplicantType(typeOfApplicant));
    localStorage.setItem(`consentAadhaarType:${trackingId}`, typeOfApplicant);
    setIdentityLoading(`aadhaar:${row.key}`);
    try {
      const redirectUrl = new URL(window.location.href);
      // redirectUrl.searchParams.set("document_verified", "true");
      // Keep the actual page/host that launched verification. HTTPS is only
      // needed for local development; deployed environments already use it.
      const url = redirectUrl.protocol === "http:"
        ? redirectUrl.toString().replace(/^http:/, "https:")
        : redirectUrl.toString();
      const response: any = await applicantDetailsServices.digilockerApplication({
        applicantEmail: row.email,
        applicantName: row.fullName,
        applicantMobile: row.mobile,
        redirectUrl: url,
        landingPageNotRequired: true,
        trackingId,
        docs: ["ADHAR"],
        typeOfApplicant,
      });
      const link = response?.data?.data?.link;
      const documentId = response?.data?.data?.applicationNo ||
        response?.data?.data?.utilityDecentroAppNo ||
        response?.data?.data?.documentId;
      if (!link || !documentId) throw new Error("Unable to start Aadhaar verification.");
      // Keep this write scoped to a key unique to this row/tracking-id only. The generic
      // "applicationNo"/aadhaarApplicationNo(CoApplicant) redux/localStorage keys are also
      // written and consumed by ApplicantTabs' own DigiLocker document flow and by the
      // DynamicForm/FormEdit stepper's in-form identity verification, which caused this
      // Aadhaar verification (initiated from Consent) to be clobbered/consumed by one of
      // those unrelated flows when this page is rendered inside AdditionalSummaryDetail.
      localStorage.setItem(`consentAadhaar:${trackingId}:${row.key}`, JSON.stringify({ documentId }));
      // Force redux-persist to flush "additionalSummaryActiveTab: 1" (set above) to
      // sessionStorage before navigating away — otherwise the debounced persist write can
      // lose the race with window.location.replace, and the page comes back on the
      // Documents tab instead of Consent.
      await persistor.flush();
      window.location.replace(/^https?:\/\//i.test(link) ? link : `https://${link}`);
    } catch (error: any) {
      // Clear the in-flight marker on failure so a failed initiation doesn't permanently
      // block other DigiLocker flows (e.g. ApplicantTabs' document upload) for this trackingId.
      localStorage.removeItem(`consentAadhaarType:${trackingId}`);
      setIdentityLoading("");
      setSnackbar({ open: true, severity: "error", message: error?.response?.data?.message || error?.message || "Unable to start Aadhaar verification." });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("document_verified") !== "true" || !trackingId) return;

    const storedType = localStorage.getItem(`consentAadhaarType:${trackingId}`) || "";
    const typeOfApplicant = documentApplicantType || storedType || "Applicant";
    const coApplicantNumber = typeOfApplicant.match(/Co-Applicant\s*(\d+)/i)?.[1];
    const rowKey = coApplicantNumber ? `coapplicant${coApplicantNumber}` : "applicant";
    const row = rows.find((item) => item.key === rowKey);
    const storageKey = `consentAadhaar:${trackingId}:${rowKey}`;
    const stored = JSON.parse(localStorage.getItem(storageKey) || "null");
    // Read only from this row's own namespaced key. Falling back to the generic
    // aadhaarApplicationNo(CoApplicant)/"applicationNo" keys is what let unrelated
    // DigiLocker flows (ApplicantTabs' document upload, DynamicForm/FormEdit's in-form
    // identity verification) hijack or clear this callback when rendered inside
    // AdditionalSummaryDetail.
    const documentId = stored?.documentId || stored?.applicationNo || stored?.utilityDecentroAppNo || "";
    if (!row || !documentId) return;
    const callbackKey = `${trackingId}:${typeOfApplicant}:${documentId}`;
    if (aadhaarCallbackProcessingRef.current.has(callbackKey)) return;
    aadhaarCallbackProcessingRef.current.add(callbackKey);
    setIdentityLoading(`aadhaar:${rowKey}`);
    applicantDetailsServices.verifyAadhaarApplication({
      userType: "applicant",
      documentId,
      employmentCategory: formData?.employmentType || formData?.applicantType || "",
      typeOfApplicant,
      formSubmissionTrackingId: trackingId,
      isListingPage: true,
    }).then(async (response: any) => {
      if (response?.data?.status !== "success") return;
      if (!row) return;
      if (!response?.data?.data?.isAadhaarConsent) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Uh-oh! We are experiencing some technical issues fetching Aadhaar details. Please try again later.",
        });
        return;
      }
      const uploadedDocuments = response?.data?.data?.uploadedDocuments || [];
      const { applicantId, eventPrefix } = await resolveVerificationContext(row);
      for (const document of uploadedDocuments) {
        if (!document?.docNameDms || !document?.key || Number(applicationId) <= 0) continue;
        const docNamed = String(document.docName || document.docNameDms || "DOCUMENT")
          .replace(/\s+/g, "")
          .toUpperCase();
        const pushPayload: any = {
          eventName: `${eventPrefix}_${docNamed}_PUSH_DOCUMENT`,
          trackingId,
          docType: document.docNameDms,
          applicantId: Number(applicantId) || 0,
          applicationId: Number(applicationId),
          documentFilePath: document.key,
          userApplicantType: typeOfApplicant,
        };
        if (document.docType === "PANCR") pushPayload.applicantHasPanCardFlag = true;
        await eligibilityCheckServices.pushDocument(pushPayload);
      }
      const proofDetails = {
        ...(response?.data?.data?.proofOfAddress || {}),
        ...(response?.data?.data?.proofOfIdentity || {}),
      };
      const normalizedDetails = {
        ...proofDetails,
        careOf: proofDetails?.careOf || "",
      };
      const coApplicantNumber = rowKey.match(/coapplicant(\d+)/i)?.[1];
      if (rowKey === "applicant") {
        dispatch(updateFormData({ isAadhaarVerified: true, aadhaar: documentId }));
        dispatch(setAadhaarVerifiedDetails(normalizedDetails));
        dispatch(setIsDocumentVerified(true));
        dispatch(setAadhaarApplicationNo(documentId));
      } else if (coApplicantNumber) {
        dispatch(updateFormData({
          [`basicCoApplicant${coApplicantNumber}IsAadhaarVerified`]: true,
          [`basicCoApplicant${coApplicantNumber}AadhaarVerified`]: true,
        }));
        dispatch(setAadhaarVerifiedDetailsCoApplicant({
          coAppKey: `CoApplicant${coApplicantNumber}`,
          details: normalizedDetails,
        }));
        dispatch(setIsDocumentVerifiedCoApplicant({
          coAppKey: `CoApplicant${coApplicantNumber}`,
          value: true,
        }));
        dispatch(setAadhaarApplicationNoCoApplicant({
          coAppKey: `CoApplicant${coApplicantNumber}`,
          applicationNo: documentId,
        }));
      }
      await persistIdentityVerification(row, "ADHAAR", {
        documentId,
        isAadhaarConsent: response?.data?.data?.isAadhaarConsent,
        proofOfAddress: response?.data?.data?.proofOfAddress,
        applicantInfo: {
          ...normalizedDetails,
          adhaar: documentId,
        },
      });
      setAadhaarVerifiedMap((current) => ({ ...current, [rowKey]: true }));
      localStorage.removeItem(storageKey);
      params.delete("document_verified");
      localStorage.removeItem(`consentAadhaarType:${trackingId}`);
      const query = params.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
      setSnackbar({ open: true, severity: "success", message: "Aadhaar verified successfully." });
    }).catch((error: any) => {
      setSnackbar({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || "Aadhaar verification failed.",
      });
    }).finally(() => {
      aadhaarCallbackProcessingRef.current.delete(callbackKey);
      setIdentityLoading("");
    });
  }, [trackingId, rows, documentApplicantType]);

  const allVerified = visibleRows.length > 0 && visibleRows.every(
    (r) => emailVerifiedMap[r.key] && mobileVerifiedMap[r.key],
  );

  useEffect(() => {
    dispatch(setAdditionalSummaryConsentComplete(allVerified));
  }, [allVerified, dispatch]);

  const shouldShowConsentMessage = (() => {
    const rowKey = otpModal.rowKey;
    if (!rowKey) return false;

    const hasConsent = hasConsentForRow(rowKey);
    const isEmailDone = !!emailVerifiedMap[rowKey] || hasConsent;
    const isPhoneDone = !!mobileVerifiedMap[rowKey] || hasConsent;
    return !isEmailDone && !isPhoneDone;
  })();
  return (
    <Box sx={pageTypeSummary == "summary" || pageTypeSummary == "loansummary" ? {
      padding: { xs: "0px ", sm: "15px" },
    } : { padding: { xs: isDeviceTypeMobile(isXsOrSm) ? "0px 0px 0px 0px" : "0px 0px 0px 16px", sm: "15px" }  }
    }>
      {visibleRows.map((row,index)=> (
        <Box
          key={row.key}
          sx={{
            mb: 1.5,
            p: 1.2,
            borderRadius: "4px",
            backgroundColor: "#f4f5fb",
            border: "1px solid #e1e3eb",
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#8a8998", fontWeight: 600, mb: 1, pb: 1, borderBottom: "1px solid #d9dbe4" }}>
            {row.applicantType}: <strong style={{ color: "#252333" }}>{row.fullName}</strong>
          </Typography>

          <Box sx={{ mt: 0.6, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: { xs: 0, sm: 4 }, rowGap: 0.8 }}>
            <Box sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "space-between", sm: "flex-start" },
              width: "100%",
              fontSize: "10px",
              color: "#4b465c",
              gap: 1,
            }}>
              <Typography sx={{ fontSize: "10px", color: "#4b465c" }}>
                Email: <strong>{row.email || "-"}</strong>
              </Typography>
              {(partnerStartedJourney===false || row?.emailVerified) &&
                <Button
                  onClick={() => partnerStartedJourney===false? handleRequestOtp(row, "email"):{}}
                  disabled={emailVerifiedMap[row.key] || !row.email}
                  sx={{
                    backgroundColor: emailVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                    color: emailVerifiedMap[row.key] ? "#28C76F" : "#000000",
                    "&:hover": {
                      backgroundColor: emailVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                      color: emailVerifiedMap[row.key] ? "#28C76F" : "#000000",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: emailVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                      color: emailVerifiedMap[row.key] ? "#28C76F" : "#000000",
                      WebkitTextFillColor: emailVerifiedMap[row.key] ? "#28C76F" : "#000000",
                      opacity: 1,
                    },
                    textTransform: "none",
                    fontSize: emailVerifiedMap[row.key] ? "12px" : "10px",
                    fontWeight: emailVerifiedMap[row.key] ? 500 : 700,
                    px: "8px",
                    ml: 0.6,
                    minWidth: "74px",
                    borderRadius: "4px",
                    gap: "6px",
                    height: "24px",
                    marginLeft: { xs: "auto", sm: 0.6 },
                  }}
                >
                  {emailVerifiedMap[row.key] ? (
                    <>
                      <img
                        src={greenCheckVerified}
                        alt="Verified"
                        style={{ height: "12px", objectFit: "contain" }}
                      />
                      <Typography variant="inherit" sx={{ fontSize: "11px" }}>
                        Verified
                      </Typography>
                    </>
                  ) : (
                    partnerStartedJourney===false &&
                    <>
                      <Typography variant="inherit" sx={{ fontSize: "10px" }}>
                        Verify
                      </Typography>
                      <img
                        src={arrowRightVerify}
                        alt="Verify"
                        style={{ height: "12px", objectFit: "contain" }}
                      />
                    </>
                  )}
                </Button>
              }
            </Box>

            <Box sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "space-between", sm: "flex-start" },
              width: "100%",
              fontSize: "10px",
              color: "#4b465c",
              gap: 1,
            }}>
              <Typography sx={{ fontSize: "10px", color: "#4b465c" }}>
                Mobile: <strong>{row.mobile || "-"}</strong>
              </Typography>
              {(partnerStartedJourney===false ||row?.mobileVerified) &&
                <Button
                  onClick={() => partnerStartedJourney===false ? handleRequestOtp(row, "mobile") : {}}
                  disabled={mobileVerifiedMap[row.key] || !row.mobile}
                  sx={{
                    backgroundColor: mobileVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                    color: mobileVerifiedMap[row.key] ? "#28C76F" : "#000000",
                    "&:hover": {
                      backgroundColor: mobileVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                      color: mobileVerifiedMap[row.key] ? "#28C76F" : "#000000",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: mobileVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                      color: mobileVerifiedMap[row.key] ? "#28C76F" : "#000000",
                      WebkitTextFillColor: mobileVerifiedMap[row.key] ? "#28C76F" : "#000000",
                      opacity: 1,
                    },
                    textTransform: "none",
                    fontSize: mobileVerifiedMap[row.key] ? "12px" : "10px",
                    fontWeight: mobileVerifiedMap[row.key] ? 500 : 700,
                    px: "8px",
                    ml: 0.6,
                    minWidth: "74px",
                    borderRadius: "4px",
                    gap: "6px",
                    height: "24px",
                    marginLeft: { xs: "auto", sm: 0.6 },
                  }}
                >
                  {mobileVerifiedMap[row.key] ? (
                    <>
                      <img
                        src={greenCheckVerified}
                        alt="Verified"
                        style={{ height: "12px", objectFit: "contain" }}
                      />
                      <Typography variant="inherit" sx={{ fontSize: "11px" }}>
                        Verified
                      </Typography>
                    </>
                  ) : (
                    partnerStartedJourney===false &&
                    <>
                      <Typography variant="inherit" sx={{ fontSize: "10px" }}>
                        Verify
                      </Typography>
                      <img
                        src={arrowRightVerify}
                        alt="Verify"
                        style={{ height: "12px", objectFit: "contain" }}
                      />
                    </>
                  )}
                </Button>
              }
            </Box>
          </Box>
          {shouldShowIdentity(row) && <Box sx={{ mt: 1.4, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: { xs: 0, sm: 4 }, rowGap: 1.2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ fontSize: "10px", color: "#777487", fontWeight: 600, flexShrink: 0 }}>PAN:</Typography>
              {panVerifiedMap[row.key] ? (
                <>
              <Typography sx={{ fontSize: "12px", color: "#000", fontWeight: 600, flexShrink: 0 }}>{maskValue(panValueMap[row.key], 'pan')}</Typography>
                  <Button
                  disabled
                  sx={{
                    backgroundColor: "transparent",
                    color: "#28C76F",
                    "&.Mui-disabled": { backgroundColor: "transparent", color: "#28C76F", WebkitTextFillColor: "#28C76F", opacity: 1 },
                    textTransform: "none", fontSize: "12px", fontWeight: 500, px: "8px", minWidth: "74px",
                    borderRadius: "4px", gap: "6px", height: "24px",
                  }}
                >
                  <img src={greenCheckVerified} alt="Verified" style={{ height: "12px", objectFit: "contain" }} />
                  <Typography variant="inherit" sx={{ fontSize: "11px" }}>Verified</Typography>
                </Button>
                </>
              ) : panValueMap[row.key] === 'no' ? <Typography sx={{ fontSize: "10px", color: "#777487", fontWeight: 600, flexShrink: 0 }}>Pan was selected as no, to verify select pan first</Typography> : (
                <>
                  <TextField
                    size="small"
                    placeholder="Enter PAN"
                    value={panValueMap[row.key] || ""}
                    disabled={true}
                    onChange={(event) => setPanValueMap((current) => ({
                      ...current,
                      [row.key]: event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10),
                    }))}
                    sx={{ width: "180px", backgroundColor: "#fff", "& .MuiInputBase-input": { py: 0.7, fontSize: "12px" } }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleVerifyPan(row)}
                    // Enable PAN verify only once this row's email and mobile are verified
                    // (read the live maps, which update right after OTP verification)
                    disabled={
                      identityLoading !== "" ||
                      !panValueMap[row.key] ||
                      !(emailVerifiedMap[row.key] ?? row.emailVerified) ||
                      !(mobileVerifiedMap[row.key] ?? row.mobileVerified) ||
                      isSubmittedApplication ||
                      partnerStartedJourney===true
                    }
                    sx={{
                      backgroundColor: "#FEC10D", color: "#000000", "&:hover": { backgroundColor: "#FEC10D", color: "#000000" },
                      "&.Mui-disabled": { backgroundColor: "#FEC10D", color: "#000000", WebkitTextFillColor: "#000000", opacity: 0.55 },
                      textTransform: "none", fontSize: "10px", fontWeight: 700, px: "8px", ml: 0.6,
                      minWidth: "74px", borderRadius: "4px", gap: "6px", height: "24px",
                    }}
                  >
                    {identityLoading === `pan:${row.key}` ? <CircularProgress size={15} sx={{ color: "#111" }} /> : <><Typography variant="inherit" sx={{ fontSize: "10px" }}>Verify</Typography><img src={arrowRightVerify} alt="Verify" style={{ height: "12px", objectFit: "contain" }} /></>}
                  </Button>
                </>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ fontSize: "10px", color: "#777487", fontWeight: 600, flexShrink: 0 }}>Aadhaar:</Typography>
              {aadhaarVerifiedMap[row.key] ? (
             <>
             <Button
                  disabled
                  sx={{
                    backgroundColor: "transparent",
                    color: "#28C76F",
                    "&.Mui-disabled": { backgroundColor: "transparent", color: "#28C76F", WebkitTextFillColor: "#28C76F", opacity: 1 },
                    textTransform: "none", fontSize: "12px", fontWeight: 500, px: "8px", minWidth: "74px",
                    borderRadius: "4px", gap: "6px", height: "24px",
                  }}
                >
                  <img src={greenCheckVerified} alt="Verified" style={{ height: "12px", objectFit: "contain" }} />
                  <Typography variant="inherit" sx={{ fontSize: "11px" }}>Verified</Typography>
                </Button> <Typography sx={{ fontSize: "10px", color: "#808390", fontWeight: 600, flexShrink: 0 }}>via DigiLocker</Typography>
                </>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleVerifyAadhaar(row)}
                  disabled={identityLoading !== "" || !row.email || !row.mobile || isSubmittedApplication || partnerStartedJourney===true}
                  sx={{
                    backgroundColor: "#FEC10D", color: "#000000", "&:hover": { backgroundColor: "#FEC10D", color: "#000000" },
                    "&.Mui-disabled": { backgroundColor: "#FEC10D", color: "#000000", WebkitTextFillColor: "#000000", opacity: 0.55 },
                    textTransform: "none", fontSize: "10px", fontWeight: 700, px: "8px", ml: 0.6,
                    minWidth: "74px", borderRadius: "4px", gap: "6px", height: "24px",
                  }}
                >
                  {identityLoading === `aadhaar:${row.key}` ? <CircularProgress size={15} sx={{ color: "#111" }} /> : <><Typography variant="inherit" sx={{ fontSize: "10px" }}>Verify</Typography><img src={arrowRightVerify} alt="Verify" style={{ height: "12px", objectFit: "contain" }} /></>}
                </Button>
              )}
            </Box>
          </Box>}
        </Box>
      ))}
      <ModalPopup
        isOpen={otpModal.open}
        onClose={() => setOtpModal((prev) => ({ ...prev, open: false }))}
        title=""
        isOtpModal={true}
      >
        <OtpInputForm
          emailMasked={otpModal.type === "email" ? otpModal.value : undefined}
          mobileMasked={otpModal.type === "mobile" ? otpModal.value : undefined}
          onOtpSubmit={handleVerifyOtp}
          onResendOtp={async () => {
            const row = visibleRows.find((r) => r.key === otpModal.rowKey);
            if (!row) return;
            await handleRequestOtp(row, otpModal.type);
          }}
          errorMessage={""}
          setErrorMsg={() => {}}
          showConsentMessage={shouldShowConsentMessage}
        />
      </ModalPopup>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConsentDetails;
